import type {ComponentProps, CSSProperties, ReactNode} from "react";
import {forwardRef, useEffect} from "react";
import {Link} from "react-router-dom";
import {useSpring, animated} from "react-spring";
import {useGetNodeRefFromRef} from "@cdx/common";
import type {ElementTheme} from "../../css/themes/themes.css";
import {elementThemes, hoverElementThemes} from "../../css/themes/themes.css";
import cx from "../../utils/cx";
import {css, Row} from "../Box/Box";
import {usePendingClick, useReactiveIcon} from "./button-helpers";
import {buttonStyles as styles} from "./DSButton.css";

type ButtonStyles = typeof styles;
type ButtonState = "initial" | "loading" | "success" | "error";

type ButtonTagProps = {
  href?: undefined;
  to?: undefined;
  onClick?: null | ((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => unknown);
  onMetaClick?: ComponentProps<"button">["onClick"];
  forwardProps?: Omit<
    ComponentProps<"button">,
    "className" | "onClick" | "type" | "disabled" | "style"
  >;
  state?: ButtonState;
  skipSuccessIcon?: boolean;
  layout?: keyof ButtonStyles["layout"];
  noErrorHandling?: boolean;
} & Pick<ComponentProps<"button">, "type">;

type ATagProps = {
  href: string;
  to?: undefined;
  layout?: undefined;
  forwardProps?: Omit<ComponentProps<"a">, "className">;
} & Pick<ComponentProps<"a">, "target" | "rel">;

type LinkTagProps = {
  href?: undefined;
  to: NonNullable<ComponentProps<Link>["to"]>;
  layout?: undefined;
  forwardProps?: Omit<ComponentProps<"a">, "className">;
};

type SharedProps = {
  variant?: keyof ButtonStyles["variant"];
  size?: keyof ButtonStyles["size"];
  active?: boolean;
  theme?: ElementTheme;
  className?: string;
  style?: CSSProperties;
  title?: string;
  disabled?: boolean;
  negatePadding?: boolean;
  autoFocus?: boolean;
  /**
   * For secondary and tertiary styles, force a solid background
   */
  solidBackgroundColor?: boolean;
  /**
   * For secondary and tertiary styles, force subtle variant
   */
  forceSubtle?: boolean;
  disabledType?: keyof ButtonStyles["disabledType"];
  hoverIcon?: ReactNode;
  hoverTheme?: ElementTheme;
};

export type DSButtonBaseProps = SharedProps;

type BaseProps = SharedProps & (ButtonTagProps | ATagProps | LinkTagProps);

export const getButtonClass = ({
  size = "md",
  theme,
  variant = "primary",
  className,
  active,
  state,
  negatePadding,
  contentPadding,
  layout = "default",
  forceSubtle,
  disabledLink,
  solidBackgroundColor,
  disabledType = "default",
  hoverTheme,
}: Pick<
  BaseProps,
  | "theme"
  | "variant"
  | "className"
  | "negatePadding"
  | "layout"
  | "forceSubtle"
  | "solidBackgroundColor"
  | "disabledType"
  | "hoverTheme"
> & {
  active: boolean;
  state: ButtonState;
  contentPadding: keyof ButtonStyles["contentPaddings"] | null;
  disabledLink?: boolean;
  size?: keyof ButtonStyles["size"] | null;
}): string => {
  const getTheme = (): ElementTheme => {
    if (state) {
      switch (state) {
        case "initial":
          break;
        case "loading":
          break;
        case "success":
          return "success";
        case "error":
          return "alert";
      }
    }
    if (theme) return theme;
    if (active) return "active";
    return "default";
  };
  const showSubtle =
    variant !== "primary" && (forceSubtle || active || state === "success" || state === "error");
  const getVariantClass = (): string => {
    if (showSubtle) {
      return styles.subtleVariants[variant];
    } else {
      return styles.variant[variant];
    }
  };

  const classes = cx(
    elementThemes[getTheme()],
    styles.layout[layout],
    getVariantClass(),
    size && styles.size[size],
    negatePadding && styles.negatePadding,
    state === "loading" && styles.loadingStyle,
    contentPadding && styles.contentPaddings[contentPadding],
    disabledLink && styles.disabledLink,
    solidBackgroundColor && !showSubtle && styles.solidBackgroundColor,
    styles.disabledType[disabledType],
    hoverTheme && state === "initial" && hoverElementThemes[hoverTheme],
    className
  );

  return classes;
};

const LinkButton = forwardRef<
  HTMLAnchorElement,
  SharedProps &
    (ATagProps | LinkTagProps) & {
      children: ReactNode;
      contentPadding: keyof ButtonStyles["contentPaddings"] | null;
    }
>((props, passedRef) => {
  const {
    to,
    href,
    disabled,
    forwardProps,
    size,
    theme,
    variant,
    className,
    negatePadding,
    contentPadding,
    autoFocus,
    layout: _,
    active = false,
    forceSubtle,
    solidBackgroundColor,
    disabledType,
    hoverIcon,
    hoverTheme,
    children,
    // let's ignore any user-passed value and just only use centered for now
    // buttons are centered by nature, let's make links centered as well
    ...rest
  } = props;
  const getProps = () => ({
    ...forwardProps,
    className: getButtonClass({
      size,
      theme,
      variant,
      className,
      active,
      state: "initial",
      negatePadding,
      contentPadding,
      layout: "centered",
      forceSubtle,
      solidBackgroundColor,
      disabledType,
      disabledLink: disabled,
      hoverTheme,
    }),
    ...rest,
  });
  const {ref, nodeRef} = useGetNodeRefFromRef(passedRef);
  useEffect(() => {
    if (autoFocus) {
      nodeRef.current?.focus();
    }
  }, [nodeRef, autoFocus]);
  const overlay = hoverIcon && !disabled && (
    <div className={styles.hoverIconOverlay}>{hoverIcon}</div>
  );
  if (to !== undefined) {
    return (
      <Link
        ref={ref}
        to={disabled ? "." : to}
        {...getProps()}
        {...(disabled ? {onClick: (e) => e.preventDefault()} : {})}
      >
        {children}
        {overlay}
      </Link>
    );
  } else {
    return (
      <a
        ref={ref}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        {...getProps()}
        {...(disabled ? {onClick: (e) => e.preventDefault()} : {})}
      >
        {children}
        {overlay}
      </a>
    );
  }
});

const ClickButton = forwardRef<
  HTMLButtonElement,
  SharedProps & ButtonTagProps & {children: ReactNode}
>((props, passedRef) => {
  const {
    onClick,
    active = false,
    onMetaClick,
    forwardProps,
    size,
    theme,
    variant,
    className,
    state: passedState,
    skipSuccessIcon,
    children,
    disabled,
    negatePadding,
    layout,
    autoFocus,
    type = "button",
    forceSubtle,
    solidBackgroundColor,
    disabledType,
    noErrorHandling,
    hoverIcon,
    hoverTheme,
    ...rest
  } = props;

  const {ref, nodeRef} = useGetNodeRefFromRef(passedRef);
  useEffect(() => {
    if (autoFocus) {
      nodeRef.current?.focus();
    }
  }, [nodeRef, autoFocus]);

  const {handleClick, icons, state} = usePendingClick({
    onClick: props.onClick,
    onMetaClick,
    passedState,
    skipSuccessIcon,
    noErrorHandling,
  });

  const contentStyles = useSpring({
    x: state === "initial" ? "0%" : "100%",
    scale: state === "initial" ? 1 : 0.8,
    opacity: state === "initial" ? 1 : 0,
    config: {tension: 600, friction: 44},
  });

  return (
    <button
      {...forwardProps}
      {...rest}
      disabled={disabled || state === "loading"}
      ref={ref}
      className={getButtonClass({
        size,
        theme,
        variant,
        className,
        active,
        state,
        negatePadding,
        contentPadding: "both",
        layout,
        forceSubtle,
        solidBackgroundColor,
        disabledType,
        hoverTheme,
      })}
      onClick={handleClick}
      type={type}
    >
      {icons}
      <animated.div style={contentStyles}>{children}</animated.div>
      {hoverIcon && !disabled && state === "initial" && (
        <div className={styles.hoverIconOverlay}>{hoverIcon}</div>
      )}
    </button>
  );
});

type ClickIconProps = SharedProps &
  ButtonTagProps & {
    icon: ReactNode;
    iconPosition?: "left" | "right";
    label?: string | null;
  };

const ClickIconButton = forwardRef<HTMLButtonElement, ClickIconProps>((props, passedRef) => {
  const {
    onClick,
    active = false,
    onMetaClick,
    forwardProps,
    size,
    theme,
    variant,
    className,
    state: passedState,
    icon,
    iconPosition,
    label,
    skipSuccessIcon,
    disabled,
    negatePadding,
    layout,
    autoFocus,
    type = "button",
    forceSubtle,
    solidBackgroundColor,
    disabledType,
    noErrorHandling,
    hoverIcon,
    hoverTheme,
    ...rest
  } = props;

  const {ref, nodeRef} = useGetNodeRefFromRef(passedRef);
  useEffect(() => {
    if (autoFocus) {
      nodeRef.current?.focus();
    }
  }, [nodeRef, autoFocus]);

  const isIconLeft = !iconPosition || iconPosition === "left";
  const getContentPadding = () => {
    if (!label) return null;
    return isIconLeft ? "right" : "left";
  };
  const contentPadding = getContentPadding();

  const {handleClick, reactiveIcon, state} = useReactiveIcon({
    iconEl: icon,
    onClick,
    onMetaClick,
    passedState,
    skipSuccessIcon,
    noErrorHandling,
  });

  return (
    <button
      {...forwardProps}
      {...rest}
      disabled={disabled || state === "loading"}
      ref={ref}
      className={getButtonClass({
        size,
        theme,
        variant,
        className,
        active,
        state,
        negatePadding,
        contentPadding,
        layout,
        forceSubtle,
        solidBackgroundColor,
        disabledType,
        hoverTheme,
      })}
      onClick={handleClick}
      type={type}
    >
      <IconContent icon={reactiveIcon} label={label} isIconLeft={isIconLeft} layout={layout} />
      {hoverIcon && !disabled && state === "initial" && (
        <div className={styles.hoverIconOverlay}>{hoverIcon}</div>
      )}
    </button>
  );
});

type DSButtonShellProps = Omit<SharedProps, "size"> &
  Omit<ButtonTagProps, "onClick"> & {
    children: ReactNode;
    contentPadding: keyof ButtonStyles["contentPaddings"] | null;
    state?: ButtonState;
    onClick: ComponentProps<"button">["onClick"];
    size: keyof ButtonStyles["size"] | null;
  };

export const DSButtonShell = forwardRef<HTMLButtonElement, DSButtonShellProps>((props, ref) => {
  const {
    onClick,
    active = false,
    onMetaClick,
    forwardProps,
    size,
    theme,
    variant,
    className,
    state = "initial",
    children,
    disabled,
    negatePadding,
    contentPadding,
    layout,
    forceSubtle,
    solidBackgroundColor,
    disabledType,
    hoverIcon,
    hoverTheme,
    ...rest
  } = props;

  return (
    <button
      type="button"
      {...forwardProps}
      {...rest}
      disabled={disabled || state === "loading"}
      ref={ref}
      className={getButtonClass({
        size,
        theme,
        variant,
        className,
        active,
        state,
        negatePadding,
        contentPadding,
        layout,
        forceSubtle,
        solidBackgroundColor,
        disabledType,
        hoverTheme,
      })}
      onClick={onClick}
    >
      {children}
      {hoverIcon && !disabled && state === "initial" && (
        <div className={styles.hoverIconOverlay}>{hoverIcon}</div>
      )}
    </button>
  );
});

type DSButtonProps = {children: ReactNode} & BaseProps;

export type DSButtonTagProps = DSButtonProps & ButtonTagProps;

export const DSButton = forwardRef<HTMLAnchorElement & HTMLButtonElement, DSButtonProps>(
  (props, ref) => {
    if (props.to !== undefined || props.href) {
      return (
        <LinkButton {...props} ref={ref} contentPadding="both">
          {props.children}
        </LinkButton>
      );
    } else {
      return (
        <ClickButton {...(props as SharedProps & ButtonTagProps & {children: ReactNode})} ref={ref}>
          {props.children}
        </ClickButton>
      );
    }
  }
);

const IconContent = ({
  icon,
  label,
  isIconLeft,
  layout,
}: {
  icon: ReactNode;
  label?: ReactNode;
  isIconLeft: boolean;
  layout: keyof ButtonStyles["layout"] | undefined;
}) => {
  return label ? (
    <Row sp="4px" align="center" flex={layout === "centered" ? undefined : "auto"}>
      {isIconLeft && icon}
      <span className={css({flex: "auto", textAlign: "left", minWidth: "0", overflow: "clip"})}>
        {label}
      </span>
      {!isIconLeft && icon}
    </Row>
  ) : (
    icon
  );
};

export type DSIconButtonTagProps = DSIconButtonProps & ButtonTagProps;

type DSIconButtonProps = {
  icon: ReactNode;
  iconPosition?: "left" | "right";
  label?: ReactNode;
  children?: undefined;
} & BaseProps;

export const DSIconButton = forwardRef<HTMLAnchorElement & HTMLButtonElement, DSIconButtonProps>(
  (props, ref) => {
    if (props.to || props.href) {
      const {iconPosition, icon, label, ...rest} = props;
      const isIconLeft = !iconPosition || iconPosition === "left";
      const getContentPadding = () => {
        if (!label) return null;
        return isIconLeft ? "right" : "left";
      };
      const contentPadding = getContentPadding();
      return (
        <LinkButton {...rest} contentPadding={contentPadding} ref={ref}>
          <IconContent label={label} isIconLeft={isIconLeft} icon={icon} layout={props.layout} />
        </LinkButton>
      );
    } else {
      return <ClickIconButton {...(props as ClickIconProps)} ref={ref} />;
    }
  }
);
